import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IMG = "https://images.unsplash.com/photo-1621801306185-8c0ccf9c8eb8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function InquiryPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Wedding Decoration", event_date: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill name, email and phone");
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success(t("inquiry_success"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase text-gold">{t("nav_inquiry")}</span>
        <h1 className="font-display text-4xl sm:text-5xl text-zinc-900 mt-3 leading-tight">{t("inquiry_title")}</h1>
        <p className="text-zinc-600 mt-5 leading-relaxed">{t("inquiry_sub")}</p>
        <a
          href="https://wa.me/919825708561?text=Hi%20Metrix%20Event%20Management%2C%20I%27d%20like%20to%20inquire%20about%20decoration%20for%20my%20event."
          target="_blank"
          rel="noreferrer"
          data-testid="inquiry-whatsapp-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb855] text-black font-semibold rounded-full h-12 px-6 transition-colors"
        >
          <MessageCircle size={18} /> {t("whatsapp_us")}
        </a>
        <img src={IMG} alt="Wedding" className="rounded-2xl mt-8 border border-pink-200 w-full h-64 object-cover" />
      </div>

      <div className="bg-card border border-pink-200 rounded-2xl p-6 sm:p-8">
        {done ? (
          <div className="text-center py-12" data-testid="inquiry-success">
            <div className="h-16 w-16 rounded-full gold-gradient flex items-center justify-center mx-auto"><Send size={26} className="text-black" /></div>
            <h3 className="font-display text-2xl text-zinc-900 mt-5">{t("inquiry_success")}</h3>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-700">{t("full_name")} *</Label>
                <Input data-testid="inq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
              </div>
              <div>
                <Label className="text-zinc-700">{t("phone")} *</Label>
                <Input data-testid="inq-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-700">{t("email")} *</Label>
              <Input data-testid="inq-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-700">{t("event_type")}</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger data-testid="inq-type" className="mt-2 bg-white border-pink-200 text-zinc-900"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Wedding Decoration", "Flower Shower / Cold Fire", "Varmala Entry", "Custom Setup", "Other"].map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-700">{t("event_date")}</Label>
                <Input data-testid="inq-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900 [color-scheme:light]" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-700">{t("budget")}</Label>
              <Input data-testid="inq-budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
            </div>
            <div>
              <Label className="text-zinc-700">{t("message")}</Label>
              <Textarea data-testid="inq-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" rows={4} />
            </div>
            <Button onClick={submit} disabled={loading} data-testid="submit-inquiry-btn" className="w-full gold-gradient text-white font-semibold rounded-full">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null} {t("submit_inquiry")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
